import React, { useContext, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import "./TalentRequestsPage.css";

import {
  useParams,
  useHistory,
} from "react-router-dom/cjs/react-router-dom.min";
import { TostMsg } from "../../../../App";
import { GetMasterTypeData } from "../../../../Services/Global-Services/GlobalServices";
import { getTalentRequestList, postTalentRequestSave } from "./Service/RecruitmentService";

// ---------- helpers ----------
const EMPTY_FORM = {
  Id: 0,
  DepartmentId: "",
  PositionTitle: "",
  TRPriorityId: "",
  NumberOfOpenings: 1,
  EmploymentTypeId: "",
  WorkModeId: "",
  Location: "",
  BudgetCurrencyCode: "INR",
  IsBudgetNegotiable: false,
  ReasonForHiring: "",
  Justification: "",
  ReplacingEmpId: "",
  IdealStartDate: "",
  HardDeadline: "",
  ExpectedJoiningDate: "",
  NotesForHR: "",
  Skills: "",
  ExperienceRangeId: "",
  SalaryRangeId: "",
};

// "2025-03-10T00:00:00" -> "2025-03-10" (needed for <input type="date" />)
const toDateInputValue = (value) => {
  if (!value) return "";
  const str = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  const d = new Date(str);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const toNumber = (v) =>
  v === "" || v === null || v === undefined ? 0 : Number(v);

// API record -> form state (used in edit mode)
const mapRecordToForm = (tr) => ({
  Id: tr.Id,
  DepartmentId: tr.DepartmentId || "",
  PositionTitle: tr.PositionTitle || "",
  TRPriorityId: tr.TRPriorityId || "",
  NumberOfOpenings: tr.NumberOfOpenings || 1,
  EmploymentTypeId: tr.EmploymentTypeId || "",
  WorkModeId: tr.WorkModeId || "",
  Location: tr.Location || "",
  BudgetCurrencyCode: tr.BudgetCurrencyCode || "INR",
  IsBudgetNegotiable: !!tr.IsBudgetNegotiable,
  ReasonForHiring: tr.ReasonForHiring || "",
  Justification: tr.Justification || "",
  ReplacingEmpId: tr.ReplacingEmpId || "",
  IdealStartDate: toDateInputValue(tr.IdealStartDate),
  HardDeadline: toDateInputValue(tr.HardDeadline),
  ExpectedJoiningDate: toDateInputValue(tr.ExpectedJoiningDate),
  NotesForHR: tr.NotesForHR || "",
  Skills: Array.isArray(tr.Skills)
    ? tr.Skills.map((s) => s.SkillName).join(", ")
    : tr.Skills || "",
  ExperienceRangeId: tr.ExperienceRangeId || "",
  SalaryRangeId: tr.SalaryRangeId || "",
});

// form state -> API payload
const buildPayload = (f, id) => ({
  Id: id,
  DepartmentId: toNumber(f.DepartmentId),
  PositionTitle: f.PositionTitle.trim(),
  TRPriorityId: toNumber(f.TRPriorityId),
  NumberOfOpenings: toNumber(f.NumberOfOpenings),
  EmploymentTypeId: toNumber(f.EmploymentTypeId),
  WorkModeId: toNumber(f.WorkModeId),
  Location: f.Location.trim(),
  BudgetCurrencyCode: f.BudgetCurrencyCode.trim(),
  IsBudgetNegotiable: !!f.IsBudgetNegotiable,
  ReasonForHiring: f.ReasonForHiring.trim(),
  Justification: f.Justification.trim(),
  ReplacingEmpId: toNumber(f.ReplacingEmpId),
  IdealStartDate: f.IdealStartDate || "",
  HardDeadline: f.HardDeadline || "",
  ExpectedJoiningDate: f.ExpectedJoiningDate || "",
  NotesForHR: f.NotesForHR.trim(),
  Skills: f.Skills.trim(), // comma separated string
  ExperienceRangeId: toNumber(f.ExperienceRangeId),
  SalaryRangeId: toNumber(f.SalaryRangeId),
});

// ---------- validation ----------
const TalentRequestValidation = Yup.object().shape({
  PositionTitle: Yup.string().trim().required("Position / Role is required."),
  DepartmentId: Yup.string().required("Department is required."),
  NumberOfOpenings: Yup.number()
    .typeError("Number of openings must be a number.")
    .min(1, "Number of openings must be at least 1.")
    .required("Number of openings is required."),
  ReasonForHiring: Yup.string()
    .trim()
    .required("Reason / Justification is required."),
  // optional fields
  TRPriorityId: Yup.string(),
  EmploymentTypeId: Yup.string(),
  WorkModeId: Yup.string(),
  Location: Yup.string(),
  BudgetCurrencyCode: Yup.string(),
  IsBudgetNegotiable: Yup.boolean(),
  Justification: Yup.string(),
  NotesForHR: Yup.string(),
  Skills: Yup.string(),
  ExperienceRangeId: Yup.string(),
  SalaryRangeId: Yup.string(),
  ExpectedJoiningDate: Yup.string(),
});

// small helper to show a field error
const FieldError = ({ formik, name }) =>
  formik.touched[name] && formik.errors[name] ? (
    <div className="text-danger" style={{ fontSize: ".75rem", marginTop: 2 }}>
      {formik.errors[name]}
    </div>
  ) : null;

// ---------- component ----------
function AddTalentRequest() {
  // route should be something like /talent-requests/edit/:id
  const { id } = useParams();
  const history = useHistory();
  const toster = useContext(TostMsg);
  const userDetails = JSON.parse(localStorage.getItem("User") || "{}");

  const recordId = Number(id) || 0;
  const isEdit = recordId > 0;

  const [initialData, setInitialData] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);

  const [departments, setDepartments] = useState([]);
  const [prioritystatusList, setPrioritystatusList] = useState([]);
  const [employmentTypeList, setEmploymentTypeList] = useState([]);
  const [workModeList, setWorkModeList] = useState([]);
  const [experienceRangeList, setExperienceRangeList] = useState([]);
  const [salaryRangeList, setSalaryRangeList] = useState([]);

  // ----- formik -----
  const talentForm = useFormik({
    initialValues: initialData,
    enableReinitialize: true, // so edit-mode data fills the form after fetch
    validationSchema: TalentRequestValidation,
    onSubmit: async (data, { setSubmitting }) => {
      setSubmitting(true);
      try {
        const payload = buildPayload(data, isEdit ? recordId : 0);
        const response = await postTalentRequestSave(payload);

        // adjust this success check to match your API's response format
        const ok =
          response?.Status === "Success" ||
          response?.Success === true ||
          response?.StatusCode === 200 ||
          response?.status === 200;

        if (ok) {
          toster?.current?.success(
            isEdit
              ? "Talent request updated successfully"
              : "Talent request raised successfully",
          );
          history.goBack();
        } else {
          toster?.current?.error(
            response?.Message || "Failed to save talent request",
          );
        }
      } catch (err) {
        toster?.current?.error("Failed to save talent request");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const submitting = talentForm.isSubmitting;

  // ----- master data (load once) -----
  const loadMaster = async (masterType, setter, critical = false) => {
    try {
      const response = await GetMasterTypeData({
        masterType,
        allRecords: true,
        organisationId: userDetails?.OrgId,
      });
      setter(response?.Data || []);
    } catch (error) {
      setter([]);
      if (critical) toster?.current?.error(`Failed to load ${masterType} list`);
    }
  };

  useEffect(() => {
    loadMaster("Department", setDepartments, true);
    loadMaster("Priority", setPrioritystatusList, true);
    loadMaster("EmploymentType", setEmploymentTypeList);
    loadMaster("WorkMode", setWorkModeList);
    loadMaster("ExperienceRange", setExperienceRangeList);
    loadMaster("SalaryRange", setSalaryRangeList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----- load record only in edit mode -----
  useEffect(() => {
    if (isEdit) {
      fetchTalentRequest(recordId);
    } else {
      setInitialData(EMPTY_FORM);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordId]);

  const fetchTalentRequest = async (trId) => {
    setLoading(true);
    try {
      const response = await getTalentRequestList({
        TRId: trId,
        Guid: "",
        TRStatusId: "",
        TRPriorityId: "",
        DepartmentId: "",
        RaisedByEmpId: 0,
        ApprovedByEmpId: 0,
        Search: "",
        RaisedFrom: "",
        RaisedTo: "",
        PageNumber: 1,
        PageSize: 1,
        SortBy: "",
        SortOrder: "",
      });
      const tr = response?.Data?.TalentRequests?.[0];
      if (tr) {
        setInitialData(mapRecordToForm(tr)); // enableReinitialize pushes it into formik
      } else {
        toster?.current?.error("Talent request not found");
      }
    } catch (error) {
      toster?.current?.error("Failed to load talent request");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="topbar">
        <i className="fa-solid fa-clipboard-list"></i>
        <div className="breadcrumb topbar-breadcrumb">
          <a href="#">Recruitment</a>
          <i className="fa-solid fa-chevron-right"></i>
          <strong className="topbar-current">Talent Requests</strong>
        </div>
        <div className="top-right">
          <div className="user-mini">
            <div className="top-avatar">AM</div>
            <div>
              <div className="user-name">Arun Mishra</div>
              <div className="user-role">HR Admin</div>
            </div>
          </div>
        </div>
      </div>

      <div className="container-fluid position-relation">
        <div className="talent-header Page-head">
          <div>
            <h2 className="talent-title">
              <i className="fa-solid fa-clipboard-list talent-title-icon"></i>{" "}
              {isEdit ? "Edit Talent Request" : "Add Talent Request"}
            </h2>
            <div className="breadcrumb">
              Department hiring requests awaiting HR approval and job creation
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-4">Loading…</div>
        ) : (
          <div className="Raise-talent-area">
            <div className="raise-modal-grid">
              <div className="fg">
                <label>
                  Position / Role{" "}
                  <span className="raise-modal-required">*</span>
                </label>
                <input
                  className="og-input"
                  placeholder="e.g. Senior Product Designer"
                  {...talentForm.getFieldProps("PositionTitle")}
                />
                <FieldError formik={talentForm} name="PositionTitle" />
              </div>
              <div className="fg">
                <label>
                  Department <span className="raise-modal-required">*</span>
                </label>
                <select
                  className="og-select"
                  {...talentForm.getFieldProps("DepartmentId")}
                >
                  <option value="">Select…</option>
                  {departments.map((d) => (
                    <option key={d.Id} value={d.Id}>
                      {d.Name}
                    </option>
                  ))}
                </select>
                <FieldError formik={talentForm} name="DepartmentId" />
              </div>
            </div>

            <div className="raise-modal-grid">
              <div className="fg">
                <label>
                  Number of Openings{" "}
                  <span className="raise-modal-required">*</span>
                </label>
                <input
                  type="number"
                  className="og-input"
                  min="1"
                  {...talentForm.getFieldProps("NumberOfOpenings")}
                />
                <FieldError formik={talentForm} name="NumberOfOpenings" />
              </div>
              <div className="fg">
                <label>Priority</label>
                <select
                  className="og-select"
                  {...talentForm.getFieldProps("TRPriorityId")}
                >
                  <option value="">Select…</option>
                  {prioritystatusList.map((p) => (
                    <option key={p.Id} value={p.Id}>
                      {p.Name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="raise-modal-grid">
              <div className="fg">
                <label>Employment Type</label>
                <select
                  className="og-select"
                  {...talentForm.getFieldProps("EmploymentTypeId")}
                >
                  <option value="">Select…</option>
                  {employmentTypeList.map((t) => (
                    <option key={t.Id} value={t.Id}>
                      {t.Name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="fg">
                <label>Work Mode</label>
                <select
                  className="og-select"
                  {...talentForm.getFieldProps("WorkModeId")}
                >
                  <option value="">Select…</option>
                  {workModeList.map((w) => (
                    <option key={w.Id} value={w.Id}>
                      {w.Name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="raise-modal-grid">
              <div className="fg">
                <label>Location</label>
                <input
                  className="og-input"
                  placeholder="e.g. Delhi"
                  {...talentForm.getFieldProps("Location")}
                />
              </div>
              <div className="fg">
                <label>Expected Joining Date</label>
                <input
                  type="date"
                  className="og-input"
                  {...talentForm.getFieldProps("ExpectedJoiningDate")}
                />
              </div>
            </div>

            <div className="raise-modal-grid">
              <div className="fg">
                <label>Experience Range</label>
                <select
                  className="og-select"
                  {...talentForm.getFieldProps("ExperienceRangeId")}
                >
                  <option value="">Select…</option>
                  {experienceRangeList.map((x) => (
                    <option key={x.Id} value={x.Id}>
                      {x.Name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="fg">
                <label>Salary Range</label>
                <select
                  className="og-select"
                  {...talentForm.getFieldProps("SalaryRangeId")}
                >
                  <option value="">Select…</option>
                  {salaryRangeList.map((x) => (
                    <option key={x.Id} value={x.Id}>
                      {x.Name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="raise-modal-grid">
              <div className="fg">
                <label>Currency</label>
                <input
                  className="og-input"
                  placeholder="INR"
                  {...talentForm.getFieldProps("BudgetCurrencyCode")}
                />
              </div>
              <div
                className="fg"
                style={{ display: "flex", alignItems: "flex-end" }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: ".4rem",
                  }}
                >
                  <input
                    type="checkbox"
                    name="IsBudgetNegotiable"
                    checked={talentForm.values.IsBudgetNegotiable}
                    onChange={talentForm.handleChange}
                    onBlur={talentForm.handleBlur}
                  />
                  Budget is negotiable
                </label>
              </div>
            </div>

            <div className="fg">
              <label>Skills</label>
              <input
                className="og-input"
                placeholder="e.g. React, Node.js, SQL"
                {...talentForm.getFieldProps("Skills")}
              />
            </div>

            <div className="fg">
              <label>
                Reason / Justification{" "}
                <span className="raise-modal-required">*</span>
              </label>
              <textarea
                className="og-textarea"
                rows="2"
                placeholder="Why is this position needed? Replacement, new project, team expansion…"
                {...talentForm.getFieldProps("ReasonForHiring")}
              ></textarea>
              <FieldError formik={talentForm} name="ReasonForHiring" />
            </div>

            <div className="fg">
              <label>Additional Justification</label>
              <textarea
                className="og-textarea"
                rows="2"
                placeholder="Any extra detail for the approver…"
                {...talentForm.getFieldProps("Justification")}
              ></textarea>
            </div>

            <div className="fg">
              <label>Notes for HR</label>
              <textarea
                className="og-textarea"
                rows="2"
                placeholder="Anything HR should know…"
                {...talentForm.getFieldProps("NotesForHR")}
              ></textarea>
            </div>

            <div className="raise-modal-info">
              <i className="fa-solid fa-circle-info raise-modal-info-icon"></i>
              If multiple openings are requested, HR may create a separate job
              posting for each opening.
            </div>
          </div>
        )}

        <div className="btn-row">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => history.goBack()}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={submitting || loading}
            onClick={talentForm.handleSubmit}
          >
            <i className="fa-solid fa-paper-plane"></i>{" "}
            {submitting
              ? "Saving…"
              : isEdit
                ? "Save Changes"
                : "Submit Request"}
          </button>
        </div>
      </div>
    </>
  );
}

export default AddTalentRequest;
